import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  MagnifyingGlass,
  X,
  ChatCircle,
  Brain,
  Bell,
  Paperclip,
  Clock,
  Tag,
} from "@phosphor-icons/react";
import { Input } from "../components/ui/input";
import * as API from "../lib/api";

const RECENT_KEY = "journey:recent-searches";
const MAX_RECENT = 6;

function loadRecent() {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveRecent(list) {
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, MAX_RECENT)));
  } catch {}
}

function Highlight({ text, q }) {
  if (!q) return <>{text}</>;
  const idx = text.toLowerCase().indexOf(q.toLowerCase());
  if (idx === -1) return <>{text}</>;
  return (
    <>
      {text.slice(0, idx)}
      <span className="bg-cyan-500/25 text-cyan-200 rounded px-0.5">
        {text.slice(idx, idx + q.length)}
      </span>
      {text.slice(idx + q.length)}
    </>
  );
}

function Section({ title, count, children, testId }) {
  if (!count) return null;
  return (
    <section className="mb-6" data-testid={testId}>
      <div className="flex items-center gap-2 mb-2 px-1">
        <h3 className="text-[10px] uppercase tracking-[0.18em] text-white/40 font-medium">
          {title}
        </h3>
        <span className="text-[10px] text-white/30">{count}</span>
      </div>
      <ul className="space-y-2">{children}</ul>
    </section>
  );
}

function Row({ icon: Icon, onClick, testId, children }) {
  return (
    <li>
      <button
        onClick={onClick}
        data-testid={testId}
        className="w-full text-left rounded-xl bg-[#0f0f0f] border border-white/5 hover:border-cyan-500/30 hover:bg-[#141414] px-4 py-3 flex items-start gap-3 transition-colors"
      >
        <Icon size={16} weight="duotone" className="text-cyan-400/80 mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">{children}</div>
      </button>
    </li>
  );
}

export default function Search() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [recent, setRecent] = useState(loadRecent());
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all"); // all | conversations | messages | memories | reminders | files
  const [allTags, setAllTags] = useState([]);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const debounceRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
    API.listMemoryTags().then(setAllTags).catch(() => {});
  }, []);

  const runSearch = useCallback(async (query) => {
    if (!query.trim()) {
      setResults(null);
      return;
    }
    setLoading(true);
    try {
      const r = await API.globalSearch(query.trim());
      setResults(r);
    } catch {
      setResults(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(q), 220);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [q, runSearch]);

  const commitRecent = (term) => {
    const t = term.trim();
    if (!t) return;
    const next = [t, ...recent.filter((x) => x.toLowerCase() !== t.toLowerCase())];
    setRecent(next);
    saveRecent(next);
  };

  const clearRecent = () => {
    setRecent([]);
    saveRecent([]);
  };

  const totalCount = results
    ? results.messages.length +
      results.conversations.length +
      results.memories.length +
      results.reminders.length +
      results.files.length
    : 0;

  return (
    <div className="overflow-y-auto scroll-contain" data-testid="search-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="relative mb-6">
          <MagnifyingGlass
            size={18}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40"
          />
          <Input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") commitRecent(q);
              if (e.key === "Escape") setQ("");
            }}
            placeholder="Search messages, memories, reminders, files…"
            className="bg-[#0f0f0f] border-white/5 pl-11 pr-10 h-12 text-base focus-visible:ring-cyan-500/40 rounded-2xl"
            data-testid="search-input"
          />
          {q && (
            <button
              onClick={() => setQ("")}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 hover:text-white"
              aria-label="Clear search"
              data-testid="search-clear"
            >
              <X size={16} />
            </button>
          )}
        </div>

        {q.trim() && (
          <FilterChips filter={filter} setFilter={setFilter} allTags={allTags} setQ={setQ} />
        )}

        {!q.trim() && (
          <div data-testid="search-recent">
            <div className="flex items-center justify-between px-1 mb-2">
              <h3 className="text-[10px] uppercase tracking-[0.18em] text-white/40 font-medium">
                Recent
              </h3>
              {recent.length > 0 && (
                <button
                  onClick={clearRecent}
                  className="text-[10px] text-white/40 hover:text-white"
                  data-testid="search-clear-recent"
                >
                  Clear
                </button>
              )}
            </div>
            {recent.length === 0 ? (
              <p className="text-sm text-white/40 px-1">No recent searches.</p>
            ) : (
              <div className="flex gap-1.5 flex-wrap">
                {recent.map((t) => (
                  <button
                    key={t}
                    onClick={() => setQ(t)}
                    className="inline-flex items-center px-3 py-1.5 rounded-full bg-[#0f0f0f] border border-white/5 hover:border-cyan-500/30 text-sm text-white/75 transition-colors"
                    data-testid={`search-recent-${t}`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {q.trim() && results && totalCount === 0 && !loading && (
          <div className="text-center py-12">
            <p className="text-white/50 text-sm">No matches for "{q}"</p>
          </div>
        )}

        {results && totalCount > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.15 }}
          >
            {(filter === "all" || filter === "conversations") && (
              <Section
                title="Conversations"
                count={results.conversations.length}
                testId="search-section-conversations"
              >
              {results.conversations.map((c) => (
                <Row
                  key={c.id}
                  icon={ChatCircle}
                  onClick={() => {
                    commitRecent(q);
                    navigate(`/app?c=${c.id}`);
                  }}
                  testId={`search-conv-${c.id}`}
                >
                  <div className="text-[15px] text-white/90 truncate">
                    <Highlight text={c.title} q={q} />
                  </div>
                </Row>
              ))}
            </Section>
            )}

            {(filter === "all" || filter === "messages") && (
            <Section
              title="Messages"
              count={results.messages.length}
              testId="search-section-messages"
            >
              {results.messages.map((m) => (
                <Row
                  key={m.message_id}
                  icon={ChatCircle}
                  onClick={() => {
                    commitRecent(q);
                    navigate(`/app?c=${m.conversation_id}`);
                  }}
                  testId={`search-msg-${m.message_id}`}
                >
                  <div className="text-[10px] text-white/40 mb-0.5 truncate">
                    {m.conversation_title} · {m.role}
                  </div>
                  <div className="text-sm text-white/85 leading-relaxed">
                    <Highlight text={m.snippet} q={q} />
                  </div>
                </Row>
              ))}
            </Section>
            )}

            {(filter === "all" || filter === "memories") && (
            <Section
              title="Memories"
              count={results.memories.length}
              testId="search-section-memories"
            >
              {results.memories.map((m) => (
                <Row
                  key={m.id}
                  icon={Brain}
                  onClick={() => {
                    commitRecent(q);
                    navigate(`/app/memory`);
                  }}
                  testId={`search-mem-${m.id}`}
                >
                  <div className="text-sm text-white/85 leading-relaxed">
                    <Highlight text={m.content} q={q} />
                  </div>
                  {m.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {m.tags.map((t) => (
                        <span
                          key={t}
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-cyan-500/10 border border-cyan-500/25 text-[10px] text-cyan-300"
                        >
                          <Tag size={9} weight="duotone" />
                          {t}
                        </span>
                      ))}
                    </div>
                  )}
                </Row>
              ))}
            </Section>
            )}

            {(filter === "all" || filter === "reminders") && (
            <Section
              title="Reminders"
              count={results.reminders.length}
              testId="search-section-reminders"
            >
              {results.reminders.map((r) => (
                <Row
                  key={r.id}
                  icon={r.status === "pending" ? Clock : Bell}
                  onClick={() => {
                    commitRecent(q);
                    navigate(`/app/reminders`);
                  }}
                  testId={`search-rem-${r.id}`}
                >
                  <div className="text-sm text-white/90">
                    <Highlight text={r.title} q={q} />
                  </div>
                  {r.description && (
                    <div className="text-xs text-white/50 mt-0.5 truncate">
                      <Highlight text={r.description} q={q} />
                    </div>
                  )}
                </Row>
              ))}
            </Section>
            )}

            {(filter === "all" || filter === "files") && (
            <Section
              title="Files"
              count={results.files.length}
              testId="search-section-files"
            >
              {results.files.map((f) => (
                <Row
                  key={f.id}
                  icon={Paperclip}
                  onClick={() => {
                    commitRecent(q);
                    window.open(API.downloadFileUrl(f.id), "_blank");
                  }}
                  testId={`search-file-${f.id}`}
                >
                  <div className="text-sm text-white/90 truncate">
                    <Highlight text={f.original_filename} q={q} />
                  </div>
                  <div className="text-[10px] text-white/40">{f.content_type}</div>
                </Row>
              ))}
            </Section>
            )}
          </motion.div>
        )}
      </div>
    </div>
  );
}

function FilterChips({ filter, setFilter, allTags, setQ }) {
  const types = [
    { id: "all", label: "All" },
    { id: "conversations", label: "Conversations" },
    { id: "messages", label: "Messages" },
    { id: "memories", label: "Memories" },
    { id: "reminders", label: "Reminders" },
    { id: "files", label: "Files" },
  ];
  return (
    <div className="mb-5 space-y-2" data-testid="search-filter-chips">
      <div className="flex gap-1.5 flex-wrap">
        {types.map((t) => (
          <button
            key={t.id}
            onClick={() => setFilter(t.id)}
            data-testid={`filter-type-${t.id}`}
            className={`px-3 py-1 rounded-full text-xs border transition-colors ${
              filter === t.id
                ? "bg-cyan-500/15 border-cyan-500/40 text-cyan-200"
                : "bg-transparent border-white/10 text-white/60 hover:bg-white/5"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>
      {allTags.length > 0 && (
        <div className="flex gap-1.5 flex-wrap items-center">
          <span className="text-[10px] uppercase tracking-[0.18em] text-white/30 font-medium pr-1">
            Tags
          </span>
          {allTags.slice(0, 14).map((t) => (
            <button
              key={t}
              onClick={() => setQ(t)}
              data-testid={`filter-tag-${t}`}
              className="px-2.5 py-0.5 rounded-full text-[11px] bg-cyan-500/10 border border-cyan-500/25 text-cyan-300 hover:bg-cyan-500/15"
            >
              {t}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

